import React, { useState, useContext, useEffect, useRef } from 'react';
import { StateContext } from '../../../StateProvider';
import { useNavigate } from 'react-router-dom';
import { useElements } from '@stripe/react-stripe-js';
import Popup from '../../../Utilities/components/Popup';
import CardDisplay from '../../../Utilities/components/CardDisplay/CardDisplay';
import { processStripePayment } from '../../../Utilities/api';
import { sendVerificationCode, verifyTwilioCode, updateUserPhoneNumber, updateWalletBalance } from '../../../Utilities/api';
import formatPhoneForDisplay from '../../../Utilities/functions/FormatPhoneForDisplay';
import './Checkout.css';
import sanitizeDate from '../../../Utilities/functions/SanitizeDate';
import { WhiteBackButton, PaymentMethodViewButton } from '../../../Utilities/components/Buttons';
import addCardIcon from '../../../../assets/Dashboard/Profile/components/Wallet/addCardIcon.png';

const Checkout = () => {
  const navigate = useNavigate();
  const elements = useElements();
  const submittingRef = useRef(false);
  const { user, cart, cartTotal, paymentMethods, setLoading, refreshUserData } =
    useContext(StateContext);

  function num(n) { return Number(n || 0); }
  function money(n) { return Number(n || 0).toFixed(2); }
  function cents(n) { return Math.round(Number(n || 0) * 100); }

  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [useBalance, setUseBalance] = useState(false);
  const [remainingBalance, setRemainingBalance] = useState(0);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [defaultMethod, setDefaultMethod] = useState(null);
  const [stripeCustomerId, setStripeCustomerId] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupContent, setPopupContent] = useState('');
  const [paymentErrorMessage, setPaymentErrorMessage] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoLabel, setPromoLabel] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);

  const MIN_CHARGE_USD = 0.5;

  useEffect(() => {
    if (user && user.info) {
      setEmail(user.info.email || '');
      setPhoneNumber(
        user.info.phone ? formatPhoneForDisplay(user.info.phone) : ''
      );
    }

    if (paymentMethods && user) {
      let foundDefault = null;
      if (paymentMethods.length > 0) {
        let i = 0;
        while (i < paymentMethods.length) {
          const method = paymentMethods[i];
          if (
            method.stripePaymentMethodId &&
            user.defaultPaymentMethodId &&
            String(method.stripePaymentMethodId) ===
              String(user.defaultPaymentMethodId)
          ) {
            foundDefault = method;
            break;
          }
          i = i + 1;
        }
        if (!foundDefault) foundDefault = paymentMethods[0];
      }
      setDefaultMethod(foundDefault);
      setStripeCustomerId(user.stripeCustomerId || '');
    }

    if (user && user.wallet) setRemainingBalance(num(user.wallet.balance));
    else setRemainingBalance(0);
  }, [user, paymentMethods]);

  // ---------- DETECT MEMBERSHIP / BUNDLE ----------
  function hasMembership(cart) {
    if (!cart || !Array.isArray(cart)) return false;
    let i = 0;
    while (i < cart.length) {
      const bt =
        cart[i] && cart[i].bookingType
          ? String(cart[i].bookingType).toLowerCase()
          : '';
      if (bt.indexOf('membership') !== -1) return true;
      i++;
    }
    return false;
  }

  function hasBundle(cart) {
    if (!cart || !Array.isArray(cart)) return false;
    var i = 0;
    while (i < cart.length) {
      var item = cart[i];
      var bt = "";
      if (item && item.bookingType) bt = String(item.bookingType).toLowerCase();
      if (bt.indexOf("bundle") !== -1) return true;
      i = i + 1;
    }
    return false;
  }

  // ---------- MEMBERSHIP DISCOUNT INFO ----------
  function getMembershipDiscountInfo(user) {
    if (!user) return { percent: 0, label: '' };

    let membership = null;
    if (user.clubMembership) membership = user.clubMembership;
    else if (user.ClubMemberships && user.ClubMemberships.length > 0)
      membership = user.ClubMemberships[0];

    if (membership &&
      membership.billingStatus === 'active' &&
      !membership.cancelledAt
    ) {
      if (membership.planType === 'Plus')
        return { percent: 20, label: 'Pawsible Plus Club' };
      if (membership.planType === 'Club')
        return { percent: 10, label: 'Pawsible Club' };
    }

    return { percent: 0, label: '' };
  }

  // ---------- BUNDLE DISCOUNT INFO ----------
  function getBundleDiscountInfo(user, cart) {
    if (!user) return { amount: 0, label: '' };
    if (!user.daycareBundle || !Array.isArray(user.daycareBundle))
      return { amount: 0, label: '' };

    var activeBundle = null;
    for (var i = 0; i < user.daycareBundle.length; i++) {
      var b = user.daycareBundle[i];
      if (b && b.status === 'active') {
        activeBundle = b;
        break;
      }
    }
    if (!activeBundle) return { amount: 0, label: '' };

    var daycareItem = null;
    if (cart && Array.isArray(cart)) {
      for (var j = 0; j < cart.length; j++) {
        var it = cart[j];
        var bt =
          it && it.bookingType ? String(it.bookingType).toLowerCase() : '';
        if (bt.indexOf('daycare') !== -1) {
          daycareItem = it;
          break;
        }
      }
    }
    if (!daycareItem) return { amount: 0, label: '' };

    var bookingInfo = daycareItem.bookingInfo || {};
    var dateRange = bookingInfo.dateRange || [];
    var totalDays = 1;

    if (Array.isArray(dateRange) && dateRange.length === 2) {
      var start = new Date(dateRange[0]);
      var end = new Date(dateRange[1]);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      var diffDays = (end - start) / (1000 * 60 * 60 * 24);
      if (diffDays - Math.floor(diffDays) > 0.9) diffDays = Math.floor(diffDays);
      totalDays = Math.round(diffDays) + 1;
      if (totalDays < 1) totalDays = 1;
    }

    var totalPrice = Number(daycareItem.originalPrice || 0);
    var bundleDays = activeBundle.remainingDays || 0;
    if (totalDays <= 0 || bundleDays <= 0) return { amount: 0, label: '' };

    var eligibleDays = Math.min(bundleDays, totalDays);
    var perDay = totalPrice / totalDays;
    var bundleCreditValue = Math.round(perDay * eligibleDays);

    if (bundleCreditValue > 0) {
      var label =
        (activeBundle.bundleType ? activeBundle.bundleType : 'Daycare') +
        ' Bundle Credit';
      return { amount: bundleCreditValue, label: label };
    }

    return { amount: 0, label: '' };
  }

  // ---------- UI HELPERS ----------
  function showPaymentPopup(message) {
    setPaymentErrorMessage(
      message || 'Something went wrong. Please try again.'
    );
    setPopupContent('paymentError');
    setIsPopupOpen(true);
  }

  function isGroomingLine(item) {
    const bt =
      item && item.bookingType ? String(item.bookingType).toLowerCase() : '';
    return bt.indexOf('groom') !== -1;
  }

  function effectiveLinePrice(item) {
    if (
      item &&
      typeof item.discountedPrice !== 'undefined' &&
      item.discountedPrice !== null
    )
      return num(item.discountedPrice);
    if (
      item &&
      typeof item.originalPrice !== 'undefined' &&
      item.originalPrice !== null
    )
      return num(item.originalPrice);
    return 0;
  }

  // ---------- PROMO EVALUATION ----------
  function evaluatePromo(code, subtotal) {
    const normalized = String(code || '').trim().toUpperCase();
    if (normalized === 'PAWSTEST') {
      let discount = subtotal - MIN_CHARGE_USD;
      if (discount < 0) discount = 0;
      return { valid: true, label: 'PAWSTEST', discount };
    }
    if (normalized === 'PAWSFAM') {
      const raw = 0.1 * subtotal;
      const maxAllowed = Math.max(0, subtotal - MIN_CHARGE_USD);
      const discount = Math.max(0, Math.min(raw, maxAllowed));
      if (discount > 0)
        return { valid: true, label: 'PAWSFAM 10%', discount };
    }
    return { valid: false, label: '', discount: 0 };
  }

  const nCartTotal = num(cartTotal);
  const nRemaining = num(remainingBalance);
  let groomingSubtotal = 0;

  if (cart && Array.isArray(cart)) {
    let i = 0;
    while (i < cart.length) {
      const it = cart[i];
      if (isGroomingLine(it))
        groomingSubtotal += effectiveLinePrice(it);
      i++;
    }
  }

  const serviceCharge = Number(
    (0.1 * groomingSubtotal).toFixed(2)
  );
  const totalBeforeWallet = nCartTotal + serviceCharge;

  let appliedBalanceRender = 0;
  if (useBalance)
    appliedBalanceRender = Math.min(
      nRemaining,
      totalBeforeWallet
    );

  const walletToItems = Math.min(appliedBalanceRender, nCartTotal);
  const promoBasis = Math.max(0, nCartTotal - walletToItems);

  const [promoBasisCache, setPromoBasisCache] = useState(0);
  useEffect(() => setPromoBasisCache(promoBasis), [promoBasis]);

  function applyPromo() {
    setPromoError('');
    if (hasMembership(cart)) {
      showPaymentPopup(
        'Promo codes cannot be applied to membership purchases.'
      );
      return;
    }
    const result = evaluatePromo(promoCode, promoBasis);
    if (!result.valid) {
      setPromoApplied(false);
      setPromoLabel('');
      setPromoDiscount(0);
      setPromoError('That code is not valid. Try again.');
      return;
    }
    setPromoApplied(true);
    setPromoLabel(result.label);
    setPromoDiscount(num(result.discount));
    setPromoError('');
  }

  function handleToggleUseBalance() {
    if (hasMembership(cart)) {
      showPaymentPopup(
        'Wallet credits cannot be applied to membership purchases.'
      );
      return;
    }
    setUseBalance(!useBalance);
  }

  function clearPromo() {
    setPromoApplied(false);
    setPromoLabel('');
    setPromoDiscount(0);
    setPromoError('');
    setPromoCode('');
  }

  const safePromoDiscountRender = promoApplied
    ? Math.min(Math.max(0, num(promoDiscount)), promoBasis)
    : 0;

  const finalTotalRender = Math.max(
    0,
    totalBeforeWallet -
      appliedBalanceRender -
      safePromoDiscountRender
  );

  const updatedBalanceRender =
    nRemaining - appliedBalanceRender;

  // ---------- MEMBERSHIP DISCOUNT (global, not line item) ----------
  const {
    percent: membershipDiscountPercent,
    label: membershipLabel,
  } = getMembershipDiscountInfo(user);

  let membershipDiscountAmount = 0;
  let membershipDiscountApplied = false;

  const {
    amount: bundleDiscountAmount,
    label: bundleLabel,
  } = getBundleDiscountInfo(user, cart);

  // ---------- MEMBERSHIP DISCOUNT (DISPLAY ONLY) ----------
  const originalPriceSum = cart
    ? cart.reduce((sum, item) => sum + num(item.originalPrice || 0), 0)
    : 0;

  const discountedPriceSum = cart
    ? cart.reduce(
        (sum, item) =>
          sum +
          num(
            item.discountedPrice != null
              ? item.discountedPrice
              : item.originalPrice || 0
          ),
        0
      )
    : 0;

  membershipDiscountApplied =
    membershipDiscountPercent > 0 &&
    originalPriceSum > discountedPriceSum;

  membershipDiscountAmount = membershipDiscountApplied
    ? Math.max(0, originalPriceSum - discountedPriceSum)
    : 0;

  const bundleDiscountApplied = bundleDiscountAmount > 0;

  // ---------- FORMAT BOOKING TYPE ----------
  function formatBookingType(item) {
    if (!item || !item.bookingType) return '';
    const type = String(item.bookingType);
    if (type.indexOf('Membership') !== -1) {
      const info = item.bookingInfo || {};
      const selectedPackage = info.selectedPackage || '';
      if (selectedPackage === 'Club')
        return 'Pawsible Club Membership';
      if (selectedPackage === 'Plus')
        return 'Pawsible Plus Club Membership';
      return 'Pawsible Membership';
    }
    if (type.indexOf('Gift-card-purchase') !== -1)
      return 'Gift Card';
    return type;
  }

  // ---------- SUBMIT PAYMENT ----------
  async function handleSubmit(e) {
    e.preventDefault();
    if (submittingRef.current || processing) return;

    if (!agreedToTerms || !agreedToPrivacy) {
      showPaymentPopup(
        'Please agree to the terms and privacy policy to continue.'
      );
      return;
    }

    if (
      !defaultMethod ||
      !defaultMethod.stripePaymentMethodId ||
      typeof defaultMethod.stripePaymentMethodId !== 'string' ||
      !defaultMethod.stripePaymentMethodId.startsWith('pm_')
    ) {
      showPaymentPopup(
        'Your payment method is invalid. Please update your card.'
      );
      return;
    }

    submittingRef.current = true;
    setProcessing(true);
    setLoading(true);

    try {
      const response = await processStripePayment(
        stripeCustomerId,
        defaultMethod.stripePaymentMethodId,
        cart,
        finalTotalRender,
        'usd',
        'Payment for services',
        'automatic',
        user,
        {
          useWallet: !!useBalance,
          appliedWalletCents: cents(appliedBalanceRender),
          cartLevelDiscountCents: cents(
            safePromoDiscountRender
          ),
          serviceChargeCents: cents(serviceCharge),
        }
      );

      if (response && response.status === 'succeeded') {
        await updateWalletBalance(user.uid, updatedBalanceRender);
        await refreshUserData(user.uid);

        if (hasMembership(cart)) {
          navigate('/profile/membership');
        } else if (hasBundle(cart)) {
          navigate('/store/services/daycare/booking');
        } else {
          navigate('/events');
        }
      } else {
        showPaymentPopup('Payment failed. Please try again.');
      }
    } catch (err) {
      console.error(err);
      showPaymentPopup(
        'There was an error processing your payment. Please try again.'
      );
    } finally {
      setProcessing(false);
      setLoading(false);
      submittingRef.current = false;
    }
  }

  const methodToDisplay =
    defaultMethod ||
    (paymentMethods && paymentMethods[0]) ||
    null;

  const hideNormalDiscounts = bundleDiscountApplied;

  // ============================================================
  //                  RENDER COMPONENT
  // ============================================================

  return (
    <div>
      <div className="checkout-backdrop">
        <div className="container">
          <WhiteBackButton />
          <div className="checkout-container">
            <div>
              <div className="checkout-summary-container">
                <h2>Payment Summary</h2>
                <hr className="checkout-breakline" />

                {cart && Array.isArray(cart) && cart.length > 0 ? (
                  cart.map((item, i) => {
                    const original = num(
                      item && item.originalPrice
                        ? item.originalPrice
                        : 0
                    );
                    const discounted = num(
                      item &&
                        item.discountedPrice !== null &&
                        typeof item.discountedPrice !==
                          'undefined'
                        ? item.discountedPrice
                        : original
                    );

                    const bi = item.bookingInfo || {};
                    const showDate = !!bi.dateRange;
                    const showPets =
                      bi.selectedPets &&
                      bi.selectedPets.length > 0;

                    let petsString = '';
                    if (showPets) {
                      let ps = '';
                      let pi = 0;
                      while (pi < bi.selectedPets.length) {
                        const p = bi.selectedPets[pi];
                        if (p && p.petName) {
                          if (ps.length > 0) ps += ', ';
                          ps += p.petName;
                        }
                        pi++;
                      }
                      petsString = ps;
                    }

                    function displayRange(d) {
                      const s = sanitizeDate(d).split(' - ');
                      if (!s || s.length !== 2) return '';
                      return s[0] === s[1]
                        ? s[0]
                        : s[0] + ' - ' + s[1];
                    }

                    // FIXED: Prevent duplicate discount display
                    const showLineItemDiscount =
                      original !== discounted &&
                      !membershipDiscountApplied &&
                      !bundleDiscountApplied;

                    return (
                      <div key={i}>
                        <div className="checkout-item-summary">
                          <div className="checkout-item-description">
                            <span className="checkout-item-service">
                              {formatBookingType(item)}
                              {showPets
                                ? ` - ${petsString}`
                                : ''}
                              {showDate
                                ? ` (${displayRange(
                                    bi.dateRange
                                  )})`
                                : ''}
                            </span>
                          </div>

                          <div className="checkout-item-originalPrice">
                            ${money(original)}
                          </div>
                        </div>

                        {showLineItemDiscount &&
                        !hideNormalDiscounts ? (
                          <div className="checkout-item-discountSummary">
                            <div className="checkout-item-description">
                              <div className="checkout-item-service">
                                Discount
                              </div>
                            </div>
                            <div className="checkout-item-discountedPrice">
                              -${money(original - discounted)}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                ) : (
                  <p>No items in cart</p>
                )}

                {useBalance && (
                  <div className="checkout-item-discountSummary">
                    <div className="checkout-item-description">
                      <div className="checkout-item-service">
                        Wallet Credit
                      </div>
                    </div>
                    <div className="checkout-item-discountedPrice">
                      -${money(appliedBalanceRender)}
                    </div>
                  </div>
                )}

                {bundleDiscountApplied && (
                  <div className="checkout-item-discountSummary">
                    <div className="checkout-item-description">
                      <div className="checkout-item-service">
                        {bundleLabel}
                      </div>
                    </div>
                    <div className="checkout-item-discountedPrice">
                      -${money(bundleDiscountAmount)}
                    </div>
                  </div>
                )}

                {membershipDiscountApplied && membershipDiscountAmount > 0 && (
                  <div className="checkout-item-discountSummary">
                    <div className="checkout-item-description">
                      <div className="checkout-item-service">
                        {membershipLabel} ({membershipDiscountPercent}%)
                      </div>
                    </div>
                    <div className="checkout-item-discountedPrice">
                      -${money(membershipDiscountAmount)}
                    </div>
                  </div>
                )}

                {promoApplied && safePromoDiscountRender > 0 && (
                  <div className="checkout-item-discountSummary">
                    <div className="checkout-item-description">
                      <div className="checkout-item-service">
                        Promo {promoLabel}
                      </div>
                    </div>
                    <div className="checkout-item-discountedPrice">
                      -${money(safePromoDiscountRender)}
                    </div>
                  </div>
                )}

                {serviceCharge > 0 && (
                  <div className="checkout-item-discountSummary">
                    <div className="checkout-item-description">
                      <div className="checkout-item-service">
                        Service Charge (Grooming 10%)
                      </div>
                    </div>
                    <div className="checkout-item-discountedPrice">
                      +${money(serviceCharge)}
                    </div>
                  </div>
                )}

                <hr className="checkout-breakline" />
                <div className="checkout-total-cost">
                  <strong>Total</strong> $
                  {money(finalTotalRender)}
                </div>
              </div>

              {/* WALLET SECTION */}
              <div className="checkout-payment-container">
                <h2 className="balance-text">
                  Use Wallet Balance
                </h2>
                <div className="use-balance-option">
                  <label className="balance-text">
                    Available: ${money(remainingBalance)}
                  </label>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={useBalance}
                      onChange={handleToggleUseBalance}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>

              {/* PROMO SECTION */}
              <div className="checkout-payment-container">
                <h2>Promotions</h2>
                <div className="checkout-contact-card">
                  <h3 className="checkout-card-title">Code</h3>
                  {!promoApplied ? (
                    <div className="promo-actions">
                      <input
                        type="text"
                        className="checkout-promo-input"
                        placeholder="Enter here"
                        value={promoCode}
                        onChange={(e) =>
                          setPromoCode(e.target.value)
                        }
                      />
                      <button
                        className="checkout-card-button"
                        onClick={applyPromo}
                      >
                        Apply
                      </button>
                    </div>
                  ) : (
                    <div className="promo-actions">
                      <span className="promo-applied-text">
                        Applied:{' '}
                        <strong>{promoLabel}</strong>
                      </span>
                      <button
                        className="checkout-card-button-remove"
                        onClick={clearPromo}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                {promoError && (
                  <p
                    className="checkout-card-info"
                    style={{ color: '#E63946' }}
                  >
                    {promoError}
                  </p>
                )}
              </div>

              {/* PAYMENT METHODS */}
              <div className="checkout-payment-container">
                <h2>Payment Methods</h2>

                {methodToDisplay && (
                  <div
                    onClick={() =>
                      navigate(
                        '/profile/wallet/payment-methods/edit-payment/' +
                          methodToDisplay.stripePaymentMethodId
                      )
                    }
                  >
                    <CardDisplay
                      key={methodToDisplay.id}
                      method={methodToDisplay}
                    />
                  </div>
                )}

                <hr className="wallet-payment-breakline" />

                <div className="wallet-card-display">
                  <img
                    src={addCardIcon}
                    alt="wallet-add"
                    className="wallet-card-logo"
                  />
                  <div className="wallet-card-info">
                    <div
                      className="wallet-card-number"
                      onClick={() =>
                        navigate(
                          '/profile/wallet/payment-methods/add-payment'
                        )
                      }
                    >
                      Add Card
                    </div>
                  </div>
                </div>
              </div>

              {/* POPUP */}
              {isPopupOpen && (
                <Popup
                  isOpen={isPopupOpen}
                  handleClose={() => setIsPopupOpen(false)}
                >
                  {popupContent === 'paymentError' && (
                    <div className="profile-popup">
                      <h2 className="profile-popup-header">
                        Payment Issue
                      </h2>
                      <p className="profile-popup-message">
                        {paymentErrorMessage}
                      </p>
                      <div className="profile-popup-actions-confirmation">
                        <button
                          onClick={() =>
                            setIsPopupOpen(false)
                          }
                          className="profile-popup-update-button-ok"
                        >
                          OK
                        </button>
                      </div>
                    </div>
                  )}
                </Popup>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CONFIRM SECTION */}
      <form
        className="checkout-form-container"
        onSubmit={handleSubmit}
      >
        <section className="checkout-policies-container">
          <div className="checkout-terms-and-conditions">
            <label className="checkout-policies-label">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) =>
                  setAgreedToTerms(e.target.checked)
                }
              />{' '}
              I agree to the{' '}
              <a
                href="/legal/terms-and-conditions"
                target="_blank"
                rel="noreferrer"
              >
                Terms & Conditions
              </a>
            </label>
            <label className="checkout-policies-label">
              <input
                type="checkbox"
                checked={agreedToPrivacy}
                onChange={(e) =>
                  setAgreedToPrivacy(e.target.checked)
                }
              />{' '}
              I agree to the{' '}
              <a
                href="/legal/privacy-policy"
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>
            </label>
          </div>
        </section>

        <div className="checkout-confirmation-container">
          <button
            type="submit"
            disabled={!agreedToTerms || !agreedToPrivacy || processing}
          >
            {processing ? 'Processing…' : 'Confirm & Pay'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Checkout;
